/**
 * If you use CSS `transform` to rotate an element, the element will overflow
 * its container. If you want the element to fit in the same bounding box as
 * before, you have to scale it proportionally to the rotation. This function
 * calculates that scale.
 */
export function getFitRotationScale({
  width,
  height,
  rotateDegrees,
}: {
  width: number;
  height: number;
  rotateDegrees: number;
}) {
  const theta = (rotateDegrees * Math.PI) / 180;
  const cos = Math.abs(Math.cos(theta));
  const sin = Math.abs(Math.sin(theta));

  return Math.min(
    width / (width * cos + height * sin),
    height / (width * sin + height * cos)
  );
}

function getRotatedDimensions(
  width: number,
  height: number,
  angleDegrees: number
): { width: number; height: number } {
  const angle = (angleDegrees * Math.PI) / 180; // Convert to radians
  const cos = Math.abs(Math.cos(angle));
  const sin = Math.abs(Math.sin(angle));

  const rotatedWidth = width * cos + height * sin;
  const rotatedHeight = width * sin + height * cos;

  return {
    width: rotatedWidth,
    height: rotatedHeight,
  };
}

function scaleFitIntoContainer(
  image: { width: number; height: number },
  container: { width: number; height: number }
) {
  const scaleToFitWidth = container.width / image.width;
  const scaleToFitHeight = container.height / image.height;

  return Math.min(scaleToFitHeight, scaleToFitWidth);
}

/**
 * Take an image that is assumed to already be scaled to fit into a container
 * (e.g. as with object-fit), rotate it, and then returns a scale that can be
 * used to fit the image into the container again. This is useful of using CSS
 * `transform` to rotate an element and then using `object-fit` to scale it to
 * fit into the container.
 */
export function getFitContainerRotationScale({
  image,
  container,
  rotateDegrees,
}: {
  image: { width: number; height: number };
  container: { width: number; height: number };
  rotateDegrees: number;
}) {
  const scaleToSizeBeforeRotation = scaleFitIntoContainer(image, container);

  const imageBeforeRotation = {
    width: image.width * scaleToSizeBeforeRotation,
    height: image.height * scaleToSizeBeforeRotation,
  };

  const rotatedDimensions = getRotatedDimensions(
    imageBeforeRotation.width,
    imageBeforeRotation.height,
    rotateDegrees
  );

  return scaleFitIntoContainer(rotatedDimensions, container);
}
